import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, MessageSquarePlus, Star, X } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc, formatApiError } from "../lib/api";
import { TESTIMONIALS } from "../data/site";
import { reviewsJsonLd, setJsonLd } from "../lib/useSEO";
import { FadeUp, Eyebrow } from "./Reveal";

const inputCls = "w-full rounded-xl border border-inkline bg-parchment px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-muted2/70 focus:border-spice";
const LABELS = ["", "Not for me", "Could be better", "Good", "Really good", "Loved it"];

export const StarPicker = ({ value, onChange, size = 38 }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div>
      <div className="flex gap-2" role="radiogroup" aria-label="Star rating" data-testid="review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`review-star-${n}`}
            onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => onChange(n)}
            className="transition-transform duration-200 hover:scale-110 active:scale-95">
            <Star size={size} className={n <= shown ? "text-saffron" : "text-inkline"} fill={n <= shown ? "currentColor" : "none"} strokeWidth={1.5} />
          </button>
        ))}
      </div>
      <p className="mt-2 h-5 font-mono text-xs uppercase tracking-[0.2em] text-muted2" data-testid="review-star-label">{LABELS[shown]}</p>
    </div>
  );
};

/** Small "★ 4.9 · 12 reviews" pill for the navbar; jumps to the reviews section on the home page. */
export const ReviewsBadge = ({ className = "", onNavigate }) => {
  const [summary, setSummary] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    api.get("/reviews-summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, [location.pathname]);
  if (!summary?.count) return null;
  const go = () => {
    onNavigate?.();
    if (location.pathname === "/") document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth", block: "start" });
    else navigate("/#reviews");
  };
  return (
    <button onClick={go} data-testid="nav-reviews-badge" title="Read customer reviews"
      className={`inline-flex items-center gap-1.5 rounded-full border border-saffron/50 bg-saffron/10 px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:bg-saffron/25 ${className}`}>
      <Star size={13} className="text-saffron" fill="currentColor" />
      <span className="font-mono">{summary.average.toFixed(1)}</span>
      <span className="text-ink/70">· {summary.count} review{summary.count === 1 ? "" : "s"}</span>
    </button>
  );
};

const WriteReviewDialog = ({ onClose, onDone }) => {
  const [form, setForm] = useState({ stars: 0, quote: "", name: "", role: "", website: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.stars) return toast.error("Pick a star rating first.");
    if (form.quote.trim().length < 10) return toast.error("Tell us a little more — at least 10 characters.");
    setBusy(true);
    try {
      const { data } = await api.post("/reviews", { ...form, quote: form.quote.trim(), name: form.name.trim() });
      onDone(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] flex items-end justify-center bg-ink/60 p-4 backdrop-blur-sm sm:items-center" onClick={onClose} data-testid="write-review-dialog">
      <motion.form initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-lg rounded-3xl border border-inkline bg-cream p-7 sm:p-9" data-testid="write-review-form">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-spice">Your review</p>
            <h3 className="mt-2 font-display text-2xl font-semibold text-ink">How was HiPlate?</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-2 text-muted2 hover:bg-ink/5 hover:text-ink" data-testid="write-review-close"><X size={18} /></button>
        </div>
        <div className="mt-5 space-y-4">
          <StarPicker value={form.stars} onChange={(stars) => setForm({ ...form, stars })} size={34} />
          <textarea rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} maxLength={600} placeholder="What stood out? A favorite dish, the portions, the delivery…" className={`${inputCls} resize-none`} data-testid="write-review-quote" />
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={60} placeholder="Your name" required className={inputCls} data-testid="write-review-name" />
            <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} maxLength={60} placeholder="City, e.g. Alexandria, VA (optional)" className={inputCls} data-testid="write-review-role" />
          </div>
          <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" name="website" />
          <button type="submit" disabled={busy} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-spice px-7 py-3.5 text-sm font-bold text-parchment transition-colors hover:bg-ink disabled:opacity-60" data-testid="write-review-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Star size={16} />} Post my review
          </button>
          <p className="text-xs leading-relaxed text-muted2">Honest reviews from real meals only, please. Had a problem? Call (571) 833-1906 so we can fix it first.</p>
        </div>
      </motion.form>
    </motion.div>
  );
};

const ReviewCard = ({ t, i }) => (
  <figure className="flex h-full flex-col rounded-3xl border border-inkline bg-cream p-7" data-testid={`testimonial-${i}`}>
    <div className="flex gap-1 text-saffron">
      {Array.from({ length: t.stars }).map((_, s) => <Star key={s} size={15} fill="currentColor" />)}
    </div>
    <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-ink/85">“{t.quote}”</blockquote>
    <figcaption className="mt-5 flex items-center gap-3">
      {t.img ? (
        <img src={imgSrc(t.img)} alt={t.name} className="h-11 w-11 shrink-0 rounded-full object-cover ring-2 ring-saffron/40" data-testid={`testimonial-photo-${i}`} />
      ) : (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink font-display text-base font-semibold text-saffron ring-2 ring-saffron/40">{t.name.trim()[0]?.toUpperCase()}</span>
      )}
      <div className="min-w-0">
        <div className="font-display text-base font-semibold text-ink">{t.name}</div>
        <div className="text-xs text-muted2">{t.role || "Verified HiPlate customer"}</div>
      </div>
    </figcaption>
  </figure>
);

const INITIAL = 8;

/** Home-page reviews: live list, summary header, "write a review" button, show-more. */
export const ReviewsSection = () => {
  const [items, setItems] = useState(TESTIMONIALS);
  const [showAll, setShowAll] = useState(false);
  const [writing, setWriting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/testimonials").then(({ data }) => { if (Array.isArray(data) && data.length) setItems(data); }).catch(() => {});
  }, []);
  useEffect(() => {
    const ld = reviewsJsonLd(items);
    if (ld) setJsonLd("reviews-jsonld", ld);
  }, [items]);
  useEffect(() => {
    if (location.hash !== "#reviews") return;
    // The home page plays a short intro; keep nudging until the section is in view.
    const scroll = () => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth", block: "start" });
    const timers = [400, 1500, 3000].map((ms) => setTimeout(scroll, ms));
    return () => timers.forEach(clearTimeout);
  }, [location.hash]);

  const avg = items.length ? (items.reduce((a, t) => a + t.stars, 0) / items.length).toFixed(1) : "0.0";
  const visible = showAll ? items : items.slice(0, INITIAL);

  const onDone = (data) => {
    setWriting(false);
    if (data.published && data.review) {
      setItems((cur) => [data.review, ...cur]);
      setShowAll(true);
      toast.success("Thank you! Your review is live.");
    } else {
      toast.success("Thank you! Our team will read it and publish it shortly.");
    }
  };

  return (
    <section id="reviews" className="mx-auto max-w-7xl scroll-mt-28 px-4 py-20 sm:px-8 lg:py-28" data-testid="testimonials">
      <FadeUp>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Eyebrow>Word around the DMV</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">Regulars who won't stop talking.</h2>
            <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="reviews-summary">
              <span className="flex gap-0.5 text-saffron">{Array.from({ length: 5 }).map((_, s) => <Star key={s} size={16} fill="currentColor" />)}</span>
              <span className="font-display text-lg font-semibold text-ink">{avg}</span>
              <span className="text-sm text-muted2">from {items.length} live customer review{items.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <button onClick={() => setWriting(true)} data-testid="write-review-btn"
            className="inline-flex items-center gap-2 self-start rounded-full bg-ink px-6 py-3.5 text-sm font-semibold text-parchment transition-colors hover:bg-spice lg:self-auto">
            <MessageSquarePlus size={16} /> Write a review
          </button>
        </div>
      </FadeUp>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {visible.map((t, i) => (
          <FadeUp key={t.id || t.name} delay={Math.min(i, 7) * 0.08}><ReviewCard t={t} i={i} /></FadeUp>
        ))}
      </div>
      {items.length > INITIAL && !showAll && (
        <div className="mt-10 text-center">
          <button onClick={() => setShowAll(true)} data-testid="reviews-show-more" className="rounded-full border border-inkline px-6 py-3 text-sm font-semibold text-ink transition-colors hover:border-spice hover:text-spice">
            Read all {items.length} reviews
          </button>
        </div>
      )}
      <AnimatePresence>{writing && <WriteReviewDialog onClose={() => setWriting(false)} onDone={onDone} />}</AnimatePresence>
    </section>
  );
};
